import { Textarea } from '@neo/primitives/textarea'
import { Tooltip } from '@neo/primitives/tooltip'
import { m } from 'framer-motion'
import { ThumbsDown, ThumbsUp } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { flowService } from 'v2/shared/services/flow-service'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import { vibeAgentLogger as logger } from 'v2/utils/logger'
import { DURATION, EASE_OUT } from '../motion'
import { useVibeAgentStore } from '../store'
import type { FeedbackVote } from '../wire'

const COMMENT_MAX = 500
const SAVED_HOLD_MS = 1500

// The voted state whispers: the fill alone carries it, in the same muted color
// as the idle icons, and it hides with the rest of the hover-revealed controls
// (touch keeps everything always-visible via the media query in style.scss).
const BUTTON_CLASS = [
  'vibe-hover-reveal text-secondary',
  'p-1 rounded transition-[opacity,transform] duration-150 ease-out active:scale-[0.97]',
  'hover:bg-st1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/40',
].join(' ')

/**
 * Thumbs up / down for one agent turn. The vote is written to the store first
 * and sent after; a failed request quietly restores the previous vote. A
 * thumbs-down opens one optional single-line note. Clicking the active thumb
 * clears. Rendered inline in the message footer; the note wraps onto its own
 * line beneath the icons.
 */
export const FeedbackControls = ({ responseId }: { responseId: string }) => {
  const sessionId = useVibeAgentStore((s) => s.sessionId)
  const vote = useVibeAgentStore((s) => s.feedback[responseId])
  const setFeedback = useVibeAgentStore((s) => s.setFeedback)
  const clearFeedback = useVibeAgentStore((s) => s.clearFeedback)
  const [noteOpen, setNoteOpen] = useState(false)
  const [note, setNote] = useState('')
  const [popKey, setPopKey] = useState(0)
  const [saved, setSaved] = useState(false)
  const noteRef = useRef<HTMLTextAreaElement>(null)
  const downRef = useRef<HTMLButtonElement>(null)
  // Writes are serialized per turn (this component is per-responseId): each
  // request starts only after the previous settles, so the server applies
  // them in click order. The sequence number lets only the LATEST request
  // roll back — and it rolls back to the last state the SERVER confirmed
  // (seeded from the snapshot at mount), never to click-time optimistic
  // state, which may itself have been rejected.
  const opSeqRef = useRef(0)
  const chainRef = useRef<Promise<unknown> | null>(null)
  const confirmedRef = useRef<FeedbackVote | undefined>(vote)
  // A pointer press on a thumb fires blur first; the click owns that path.
  // Tab away is not a click — it must still save (hence a press flag, not
  // relatedTarget sniffing).
  const thumbPressRef = useRef(false)
  // Escape means discard: closeNote() moves focus, and that blur must not
  // post the draft the user just chose to throw away. Consumed by onBlur.
  const discardRef = useRef(false)

  useEffect(() => {
    if (noteOpen) noteRef.current?.focus()
  }, [noteOpen])

  useEffect(() => {
    if (!saved) return
    const timer = setTimeout(() => setSaved(false), SAVED_HOLD_MS)
    return () => clearTimeout(timer)
  }, [saved])

  const send = useCallback(
    (next: FeedbackVote | null): Promise<boolean> => {
      if (!sessionId) return Promise.resolve(false)
      const op = ++opSeqRef.current
      const run = async (): Promise<boolean> => {
        try {
          await flowService.postVibeFeedback(sessionId, {
            response_id: responseId,
            thumbs_up: next ? next.thumbs_up : null,
            comment: next?.comment ?? null,
          })
          confirmedRef.current = next ?? undefined
          track(AnalyticsEvents.VIBE_FEEDBACK, {
            rating: next ? (next.thumbs_up ? 'up' : 'down') : 'cleared',
          })
          return true
        } catch (error) {
          logger.warn('[FEEDBACK] request failed', { responseId, error })
          if (op === opSeqRef.current) {
            const confirmed = confirmedRef.current
            if (confirmed) setFeedback(responseId, confirmed)
            else clearFeedback(responseId)
          }
          return false
        }
      }
      const base = chainRef.current ?? Promise.resolve()
      const result = base.then(run, run)
      chainRef.current = result
      return result
    },
    [sessionId, responseId, setFeedback, clearFeedback],
  )

  const cast = (thumbsUp: boolean) => {
    if (vote?.thumbs_up === thumbsUp) {
      clearFeedback(responseId)
      setNoteOpen(false)
      void send(null)
      return
    }
    const next: FeedbackVote = {
      thumbs_up: thumbsUp,
      comment: thumbsUp ? null : (vote?.comment ?? null),
    }
    setFeedback(responseId, next)
    setPopKey((k) => k + 1)
    setNoteOpen(!thumbsUp)
    if (!thumbsUp) {
      setNote(vote?.comment ?? '')
      discardRef.current = false
    }
    void send(next)
  }

  const saveNote = () => {
    const trimmed = note.trim().slice(0, COMMENT_MAX)
    setNoteOpen(false)
    if (!vote || vote.thumbs_up || (vote.comment ?? '') === trimmed) return
    const next: FeedbackVote = { thumbs_up: false, comment: trimmed || null }
    setFeedback(responseId, next)
    void send(next).then((ok) => {
      // Confirmation only after the server agreed; a failure already rolled
      // the vote back silently.
      if (ok) setSaved(true)
    })
  }

  const closeNote = () => {
    discardRef.current = true
    setNoteOpen(false)
    downRef.current?.focus()
  }

  const markThumbPress = () => {
    thumbPressRef.current = true
    // Cleared on the next macrotask: blur (the only reader) fires within the
    // same gesture, and a press that never completes a click must not eat a
    // later, unrelated blur save.
    setTimeout(() => {
      thumbPressRef.current = false
    }, 0)
  }

  const isUp = vote?.thumbs_up === true
  const isDown = vote?.thumbs_up === false

  return (
    <>
      <Tooltip content="Good response" delayDuration={300}>
        <button
          type="button"
          onClick={() => cast(true)}
          onPointerDown={markThumbPress}
          className={BUTTON_CLASS}
          aria-label="Good response"
          aria-pressed={isUp}
          data-testid="button-feedback-up"
        >
          <span key={isUp ? popKey : 'idle'} className={isUp ? 'block vibe-vote-pop' : 'block'}>
            <ThumbsUp className="w-3.5 h-3.5" fill={isUp ? 'currentColor' : 'none'} />
          </span>
        </button>
      </Tooltip>
      <Tooltip content="Bad response" delayDuration={300}>
        <button
          ref={downRef}
          type="button"
          onClick={() => cast(false)}
          onPointerDown={markThumbPress}
          className={BUTTON_CLASS}
          aria-label="Bad response"
          aria-pressed={isDown}
          data-testid="button-feedback-down"
        >
          <span key={isDown ? popKey : 'idle'} className={isDown ? 'block vibe-vote-pop' : 'block'}>
            <ThumbsDown className="w-3.5 h-3.5" fill={isDown ? 'currentColor' : 'none'} />
          </span>
        </button>
      </Tooltip>
      {saved && !noteOpen && (
        <m.output
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: DURATION.exit, ease: EASE_OUT }}
          className="ml-1 text-xs-500 text-secondary"
          data-testid="text-feedback-saved"
        >
          Note saved
        </m.output>
      )}
      {noteOpen && (
        <m.div
          initial={{ opacity: 0, transform: 'translateY(-4px)' }}
          animate={{ opacity: 1, transform: 'translateY(0px)' }}
          transition={{ duration: DURATION.enter, ease: EASE_OUT }}
          className="basis-full w-80 max-w-full mt-1 px-px"
        >
          <Textarea
            ref={noteRef}
            value={note}
            onChange={(e) =>
              // Single-line contract: newlines (Shift+Enter, paste) collapse
              // to spaces before they enter the value.
              setNote(e.target.value.replace(/[\r\n]+/g, ' ').slice(0, COMMENT_MAX))
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                saveNote()
              }
              if (e.key === 'Escape') closeNote()
            }}
            onBlur={() => {
              // Escape discards; a thumb press owns its own request path;
              // anything else that takes focus (Tab included) saves the note.
              if (discardRef.current) {
                discardRef.current = false
                return
              }
              if (thumbPressRef.current) return
              saveNote()
            }}
            maxLength={COMMENT_MAX}
            autoExpand
            rows={1}
            placeholder="What went wrong? (optional)"
            aria-label="What went wrong (optional)"
            className="min-h-0 h-8 py-1.5 px-2.5 rounded-lg border border-st2 bg-background text-p-400 text-foreground placeholder:text-tertiary focus-visible:outline-none focus-visible:border-st1 focus-visible:ring-1 focus-visible:ring-st1"
            data-testid="input-feedback-note"
          />
        </m.div>
      )}
    </>
  )
}
